import { useMutation, useQuery } from "@apollo/client";
import { Button } from "@blueprintjs/core";
import _ from "lodash";
import { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  CustomerOrder,
  CustomerOrder_State,
  CustomerOrder_Type,
  UpdateCustomerOrderInput,
  User,
} from "../../../../api/schema";
import { FlexBoxInline } from "../../../../components/flex/Flex";
import { ModalInputType } from "../../../../components/form/Form";
import {
  InputFormMode,
  ValuesVariables,
} from "../../../../components/form/InputForm";
import {
  FormInputFields,
  InputFormBuilder,
  InputFormBuilderState,
} from "../../../../components/form/InputFormBuilder";
import { Spinner } from "../../../../components/Spinner";
import { print_url } from "../../../../config";
import { UserLanguage } from "../../../../lang/lang";
import { ClerkPrettyRenderer } from "../../../../utils/PrettyRenderer";
import { ReactSetState } from "../../../../utils/state";
import {
  CustomerOrderViewQuery,
  UpdateCustomerOrderMutation,
} from "../graphql";
import {
  IconMapperState,
  IconMapperStateIntent,
  IconMapperType,
  IconMapperTypeIntent,
} from "../helper";
import "../style.css";
import { OrderPositions } from "./OrderPositions";

interface CustomerOrderViewControllerProps
  extends RouteComponentProps<{ id: string }> {}

interface CustomerOrderViewControllerPropsSimple {
  id: number | null;
  changes?: (b: boolean) => void;
  showHeader?: boolean;
}

export const CustomerOrderViewController = (
  props:
    | CustomerOrderViewControllerProps
    | CustomerOrderViewControllerPropsSimple
) => {
  let id;
  // console.log({ props });

  if ("match" in props) {
    const match = props.match;
    if (match && match.params.id && match.params.id.length > 0)
      id = match.params.id;
  } else {
    id = props.id;
  }

  const { loading, data, refetch } = useQuery<{
    customerOrder: CustomerOrder;
    users: User[];
  }>(CustomerOrderViewQuery, {
    variables: { id },
  });

  // console.log({ loading, error, data });

  if (id === null || id === undefined || id === "") return null;

  if (loading) return <Spinner />;
  if (data && data.customerOrder !== null)
    return (
      <CustomerOrderView
        customerOrder={data.customerOrder}
        users={data.users}
        refetch={() => refetch()}
        changes={"changes" in props ? props.changes : undefined}
        showHeader={"showHeader" in props ? props.showHeader : true}
      />
    );

  // console.log("got error", error);

  return null;
};

const OrderViewKeys = (
  id: number,
  users: User[]
): FormInputFields<CustomerOrder> => ({
  customer_contact: {
    type: ModalInputType.String,
    label: UserLanguage().data.customerOrder.customerContact,
  },
  customer_name: {
    type: ModalInputType.String,
    label: UserLanguage().data.customerOrder.customerName,
  },
  customer_reference: {
    type: ModalInputType.String,
    label: UserLanguage().data.customerOrder.customerReference,
  },
  delivery_date: {
    type: ModalInputType.Date,
  },
  id: { type: ModalInputType.FixedItem, defaultValue: id },
  notes: {
    type: ModalInputType.Text,
    label: UserLanguage().data.customerOrder.notes,
  },
  state: {
    type: ModalInputType.ItemSelect,
    icon: (i) => IconMapperState[i],
    intent: (i) => IconMapperStateIntent[i],
    items: _.values(CustomerOrder_State),
    label: UserLanguage().data.customerOrder.state,
    placeholder: UserLanguage().actions.select(
      UserLanguage().data.customerOrder.state
    ),
    defaultValue: CustomerOrder_State.accepted,
    translate: UserLanguage().data.customerOrder.states,
  },
  type: {
    type: ModalInputType.ItemSelect,
    icon: (i) => IconMapperType[i],
    intent: (i) => IconMapperTypeIntent[i],
    items: _.values(CustomerOrder_Type),
    label: UserLanguage().data.customerOrder.type,
    placeholder: UserLanguage().actions.select(
      UserLanguage().data.customerOrder.type
    ),
    defaultValue: CustomerOrder_Type.repair,
    translate: UserLanguage().data.customerOrder.types,
  },
  express: {
    type: ModalInputType.CheckBox,
    label: UserLanguage().data.customerOrder.express,
  },
  clerk: {
    type: ModalInputType.ItemSelect,
    items: users,
    icon: "user",
    label: UserLanguage().data.customerOrder.clerk,
    textRenderer: (c) => ClerkPrettyRenderer(c),
  },
  updated_at: {
    type: ModalInputType.Display,
    label: UserLanguage().data.customerOrder.updated_at,
    textRenderer: (i) => new Date(i).toLocaleString(),
  },
});

export const CustomerOrderView = ({
  customerOrder,
  refetch,
  users,
  changes,
  showHeader,
}: {
  customerOrder: CustomerOrder;
  users: User[];
  refetch: () => void;
  changes?: (v: boolean) => any;
  showHeader?: boolean;
}) => {
  const [formHasChanges, setFormHasChanges] = useState<boolean>(false);
  const [positionHasChanges, setPositionHasChanges] = useState<boolean>(false);

  console.log({ formHasChanges, positionHasChanges });

  useEffect(
    () => changes?.(positionHasChanges || formHasChanges),
    [formHasChanges, positionHasChanges, changes]
  );

  const [updateCustomerOrderMutation] = useMutation<
    CustomerOrder,
    ValuesVariables<UpdateCustomerOrderInput>
  >(UpdateCustomerOrderMutation);

  const on_form_change = (b: boolean) => setFormHasChanges(b);
  const on_position_change = (b: boolean) => setPositionHasChanges(b);

  const header =
    showHeader !== false ? (
      <div className="cow_header">
        <h2>
          {UserLanguage().pages.customerOrder.view}
          {" - "}
          <span style={{ color: "#1177FF" }}>{customerOrder.id}</span>
          {" - "}
          <a
            href={print_url + "customer/order/" + customerOrder.id}
            target="_blank"
            rel="noreferrer"
          >
            <Button intent="primary">Print!</Button>
          </a>
        </h2>
      </div>
    ) : null;

  return (
    <InputFormBuilder<CustomerOrder, CustomerOrder, UpdateCustomerOrderInput>
      mode={InputFormMode.Edit}
      input={customerOrder}
      onSuccess={() => refetch()}
      reformId={["clerk"]}
      stringyfy={[]}
      mutation={updateCustomerOrderMutation}
      keys={OrderViewKeys(customerOrder.id, users)}
      texts={UserLanguage().pages.customerOrder.actions}
      changes={on_form_change}
    >
      {(builder: InputFormBuilderState<CustomerOrder>) => (
        <div style={{ padding: 25 }}>
          {header}
          <div className="cow_form">
            <div className="cow_form_col">
              {builder.buildCards()}
              <FlexBoxInline style={{ gap: 15, alignItems: "center" }}>
                {builder.buildField("type")}
                {builder.buildField("state")}
              </FlexBoxInline>
              <FlexBoxInline style={{ gap: 15, alignItems: "center" }}>
                {builder.buildField("clerk")}
                {builder.buildField("express")}
              </FlexBoxInline>
              {builder.buildField("customer_name")}
              {builder.buildField("customer_contact")}
              {builder.buildField("notes")}
              {builder.buildField("delivery_date")}
              {builder.buildField("updated_at")}
              {builder.buildSubmitButton()}
            </div>
            <div className="cow_form_col cow_positions">
              <OrderPositions
                customer_order={customerOrder}
                on_update={refetch}
                on_change={on_position_change}
              />
            </div>
          </div>
        </div>
      )}
    </InputFormBuilder>
  );
};
